
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from "@/components/ui/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button"; 
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check, AlertCircle, Minus, Clock, Calendar, Plus, Edit, ArrowLeft, FlaskConical, RefreshCw } from "lucide-react";
import { formatDbTimestampToLocal, formatTimeForInput } from '@/lib/utils/date';
import { useChildStore } from '@/lib/store/child';
import { useAuth } from '@/hooks/use-auth';
import SleepScheduleTimeline from '@/components/sleep-schedule/SleepScheduleTimeline';
import SleepScheduleSummary from '@/components/sleep-schedule/SleepScheduleSummary';
import SleepScheduleHeader from '@/components/sleep-schedule/SleepScheduleHeader';
import DateSelector from '@/components/sleep-schedule/DateSelector';
import { SleepScheduleProvider, useSleepSchedule } from '@/components/sleep-schedule/SleepScheduleContext';
import { ManageScheduleDialog } from '@/components/sleep-schedule/ManageScheduleDialog';
import { AdherenceMetrics } from '@/components/sleep-schedule/AdherenceMetrics';
import { ComparisonSummary } from '@/components/sleep-schedule/ComparisonSummary';
import { ChildInfoHeader } from '@/components/sleep-schedule/ChildInfoHeader';
import { ChildSelector } from '@/components/dashboard/child-selector/ChildSelector';
import { logger } from '@/lib/services/logger-service';

interface WakeWindowsContentProps {
  isSpecialist?: boolean;
  caregiverId?: string;
}

function WakeWindowsContent({ isSpecialist = false, caregiverId: caregiverIdProp }: WakeWindowsContentProps) {
  const { selectedChild } = useChildStore();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { scheduleItems, loadScheduleForDate, childInfo } = useSleepSchedule();
  const [caregiverId, setCaregiverId] = React.useState<string | undefined>(caregiverIdProp);

  // Log component initialization
  React.useEffect(() => {
    logger.info('[WakeWindows] Component initialized', {
      isSpecialist,
      caregiverIdProp,
      selectedChildId: selectedChild?.id,
      selectedChildCaregiverId: selectedChild?.caregiver_id,
      userId: user?.id,
      hasScheduleItems: scheduleItems.length > 0
    });
  }, []);

  // Get caregiver ID from selected child
  React.useEffect(() => {
    if (selectedChild?.caregiver_id) {
      logger.info('[WakeWindows] Setting caregiverId from selectedChild', {
        caregiverId: selectedChild.caregiver_id,
        childId: selectedChild.id,
        childName: selectedChild.name
      });
      setCaregiverId(selectedChild.caregiver_id);
    } else {
      logger.warn('[WakeWindows] No caregiverId in selectedChild', {
        selectedChild: selectedChild ? {
          id: selectedChild.id,
          name: selectedChild.name,
          hasCaregiver: !!selectedChild.caregiver_id
        } : null
      });
    }
  }, [selectedChild]);
  
  // Load schedule when date or child changes
  React.useEffect(() => {
    logger.info('[WakeWindows] Loading schedule', {
      selectedDate: selectedDate.toISOString(),
      childId: selectedChild?.id,
      childName: selectedChild?.name,
      hasChild: !!selectedChild
    });
    
    if (selectedChild?.id) {
      loadScheduleForDate(selectedDate, selectedChild.id);
    } else {
      loadScheduleForDate(selectedDate);
    }
  }, [selectedDate, selectedChild?.id]);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (selectedChild?.id) {
        await loadScheduleForDate(selectedDate, selectedChild.id);
      } else {
        await loadScheduleForDate(selectedDate);
      }
    } finally {
      setIsRefreshing(false);
    }
  };
  
  return (
    <>
      {childInfo && (
        <ChildInfoHeader 
          childName={childInfo.name} 
          dateOfBirth={childInfo.dateOfBirth} 
        />
      )}
      
      <AdherenceMetrics />
      
      <Card className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200 mt-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <SleepScheduleHeader childName={selectedChild?.name || 'Your child'} />
            
            {/* Show manage button for specialists */}
            {isSpecialist && (
              <Button
                onClick={() => setIsManageDialogOpen(true)}
                variant="outline"
                className="border-purple-200 hover:bg-purple-50"
              >
                {scheduleItems.length === 0 ? (
                  <><Plus className="h-4 w-4 mr-2" /> Create Schedule</>
                ) : (
                  <><Edit className="h-4 w-4 mr-2" /> Modify Schedule</>
                )}
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DateSelector 
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
              />
            </div>
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              variant="outline"
              size="icon"
              className="shrink-0"
              title="Refresh actual sleep data"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <SleepScheduleTimeline date={selectedDate} />
          
          <SleepScheduleSummary date={selectedDate} />
          
          <ComparisonSummary />
        </div>
      </Card>
      
      {/* Manage Schedule Dialog */}
      <ManageScheduleDialog
        open={isManageDialogOpen}
        onOpenChange={setIsManageDialogOpen}
        caregiverId={caregiverId}
        childId={selectedChild?.id}
        selectedDate={selectedDate}
      />
    </>
  );
}

export function WakeWindows() {
  const { role, specialistId } = useAuth();
  const navigate = useNavigate();
  const { selectedChild } = useChildStore();
  const isSpecialist = role === 'specialist' && !!specialistId;
  
  // Log page access
  React.useEffect(() => {
    console.info('[WakeWindows] Page accessed', {
      role,
      specialistId,
      isSpecialist,
      selectedChildId: selectedChild?.id,
      path: window.location.pathname,
    });

    logger.info('[WakeWindows] Page accessed', {
      role,
      specialistId,
      isSpecialist,
      selectedChildId: selectedChild?.id,
      selectedChildName: selectedChild?.name,
      selectedChildCaregiverId: selectedChild?.caregiver_id
    });
  }, [role, specialistId, isSpecialist, selectedChild]);
  
  // Redirect non-specialists
  if (!isSpecialist) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground mb-6">
            Wake Windows feature is only available to specialists.
          </p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/coach-dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
      
      <PageHeader 
        heading="Wake Windows"
        subheading="Track and analyze your child's sleep schedule"
      />
      
      <Alert className="mt-6 border-amber-200 bg-amber-50">
        <FlaskConical className="h-5 w-5 text-amber-600" />
        <AlertDescription className="text-amber-900 ml-2">
          <span className="font-semibold">ALPHA Tool:</span> This feature is being actively tested by our expert pediatric sleep specialist. Feel free to jump in early at your own risk!
        </AlertDescription>
      </Alert>
      
      {!isSpecialist && (
        <Card className="p-6 mt-6">
          <ChildSelector connectToStore={true} selectOnly={false} />
        </Card>
      )}
      
      <div className="mt-8">
        <SleepScheduleProvider isSpecialist={isSpecialist} caregiverId={selectedChild?.caregiver_id}>
          <WakeWindowsContent isSpecialist={isSpecialist} caregiverId={selectedChild?.caregiver_id} />
        </SleepScheduleProvider>
      </div>
    </div>
  );
}

export default WakeWindows;
